/**
 * ScenariosSection - Orchestrates scenario list + editor for Flux EV
 *
 * Uses UnifiedScenarioTable from shared-ui with compare, expandable results,
 * and share functionality. When a scenario is selected, shows ScenarioEditor.
 */

import React, { useState } from 'react';
import {
    PlayArrow as PlayArrowIcon,
    ContentCopy as ContentCopyIcon,
    Delete as DeleteIcon,
    Share as ShareIcon,
} from '@mui/icons-material';
import {
    UnifiedScenarioTable,
    SharedPagesDashboard,
    ShareConfigDialog,
} from '@vortx/shared-ui';
import { ScenarioEditor } from '../../../components/ev';
import EVResultsPreview from '../../../components/ev/EVResultsPreview';
import EVCompareModal from '../../../components/ev/EVCompareModal';

/**
 * Format charger config summary for table display
 */
function formatChargerSummary(scenario) {
    const chargers = scenario.chargers;
    if (!chargers || chargers.length === 0) {
        return 'No chargers configured';
    }
    return chargers
        .map((c) => `${c.quantity || 1}x ${c.power_kw || 0}kW`)
        .join(', ');
}

function ScenariosSection({ scenarios, siteCapacity, projectData, onSelectScenario, onBack, activeTab }) {
    const {
        scenarios: scenarioList,
        selectedScenario,
        simulatingScenarioId,
        saveStatus,
        saveError,
        lastSavedAt,
        selectScenario,
        deselectScenario,
        createScenario,
        duplicateScenario,
        deleteScenario,
        updateScenario,
        saveScenario,
        simulateScenario,
        getSocTrajectories,
    } = scenarios;

    // Compare state
    const [selectedForCompare, setSelectedForCompare] = useState(new Set());
    const [compareOpen, setCompareOpen] = useState(false);
    const [compareScenarios, setCompareScenarios] = useState([]);

    // Share state
    const [sharedPagesOpen, setSharedPagesOpen] = useState(false);
    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [shareScenario, setShareScenario] = useState(null);

    // Use URL-based navigation if provided, otherwise fall back to inline selection
    const handleSelect = onSelectScenario || selectScenario;
    const handleBack = onBack || deselectScenario;

    const toggleCompareSelection = (scenarioId) => {
        setSelectedForCompare(prev => {
            const next = new Set(prev);
            if (next.has(scenarioId)) {
                next.delete(scenarioId);
            } else {
                next.add(scenarioId);
            }
            return next;
        });
    };

    const handleOpenCompare = () => {
        const selected = (scenarioList || []).filter(s => selectedForCompare.has(s.id));
        setCompareScenarios(selected);
        setCompareOpen(true);
    };

    const handleShareScenario = (scenario, e) => {
        e.stopPropagation();
        setShareScenario(scenario);
        setShareDialogOpen(true);
    };

    if (selectedScenario) {
        return (
            <ScenarioEditor
                scenario={selectedScenario}
                siteCapacity={siteCapacity}
                allScenarios={scenarioList}
                projectData={projectData}
                onChange={updateScenario}
                onSave={saveScenario}
                saveStatus={saveStatus}
                saveError={saveError}
                lastSavedAt={lastSavedAt}
                onBack={handleBack}
                onSimulate={() => simulateScenario(selectedScenario.id)}
                simulating={simulatingScenarioId === selectedScenario.id}
                socTrajectories={selectedScenario ? getSocTrajectories(selectedScenario.id) : null}
                activeTab={activeTab}
            />
        );
    }

    return (
        <>
            <UnifiedScenarioTable
                scenarios={scenarioList || []}
                formatConfig={formatChargerSummary}
                getHasResults={(s) => !!(s.results && Object.keys(s.results).length > 0)}
                getStatusLabel={(s, hr) => hr ? 'Simulated' : 'Not simulated'}
                getCreatedDate={(s) => s.created_at}
                onRowClick={(s) => handleSelect(s)}
                expandable={true}
                renderExpanded={(s) => <EVResultsPreview scenario={s} />}
                compareEnabled={true}
                selectedForCompare={selectedForCompare}
                onCompareToggle={toggleCompareSelection}
                onCompare={handleOpenCompare}
                actions={[
                    {
                        id: 'share',
                        icon: <ShareIcon />,
                        label: 'Share',
                        onClick: (s, e) => handleShareScenario(s, e),
                        show: (s) => !!(s.results && Object.keys(s.results).length > 0),
                    },
                    {
                        id: 'simulate',
                        icon: <PlayArrowIcon />,
                        label: 'Run Simulation',
                        onClick: (s) => simulateScenario(s.id),
                        loading: (s) => s.id === simulatingScenarioId,
                    },
                    {
                        id: 'duplicate',
                        icon: <ContentCopyIcon />,
                        label: 'Duplicate',
                        onClick: (s) => duplicateScenario(s.id),
                    },
                    {
                        id: 'delete',
                        icon: <DeleteIcon />,
                        label: 'Delete',
                        onClick: (s) => deleteScenario(s.id),
                    },
                ]}
                onCreateScenario={createScenario}
                isLoading={!scenarioList}
            />

            {/* Compare Modal */}
            <EVCompareModal
                open={compareOpen}
                onClose={() => {
                    setCompareOpen(false);
                    setSelectedForCompare(new Set());
                    setCompareScenarios([]);
                }}
                scenarios={compareScenarios}
                projectId={projectData?.id}
                projectName={projectData?.name || 'EV Project'}
            />

            {/* Shared Pages Dashboard */}
            <SharedPagesDashboard
                open={sharedPagesOpen}
                onClose={() => setSharedPagesOpen(false)}
            />

            {/* Single-Scenario Share Dialog */}
            {shareScenario && (
                <ShareConfigDialog
                    open={shareDialogOpen}
                    onClose={() => {
                        setShareDialogOpen(false);
                        setShareScenario(null);
                    }}
                    projectId={projectData?.id}
                    projectName={projectData?.name || 'EV Project'}
                    scenarioIds={[shareScenario.id]}
                    scenarioNames={[shareScenario.name]}
                    comparisonData={{ scenarios: [shareScenario], projectName: projectData?.name || 'EV Project' }}
                />
            )}
        </>
    );
}

export default ScenariosSection;
